import { AuditlogList } from "components/AuditlogList/AuditlogList";
import { Button } from "components/DesignSystem/Button/Button";
import { Link } from "components/DesignSystem/Link/Link";
import Slider from "components/DesignSystem/Slider/Slider";
import { Redirect } from "components/icons/Redirect";
import { AUDIT_LOG_CONTENT_TYPE } from "constants/auditLogFilters";
import { BOOKS_SETTINGS_TABS } from "constants/booksSettings";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import qs from "qs";
import { AuditLog } from "store/apis/auditlogs";

const getRedirectLink = ({
  log,
  isAdmin,
}: {
  log: AuditLog;
  isAdmin: boolean;
}): string | null => {
  if (
    log.obj?.content_type === AUDIT_LOG_CONTENT_TYPE.CHANNEL &&
    log.frontend_url
  ) {
    return log.frontend_url;
  }

  if (log.obj?.content_type === AUDIT_LOG_CONTENT_TYPE.COMPANY_GROUP_INVOICE) {
    if (log.company_entity) {
      return isAdmin
        ? `/crm/${log.company_group?.uuid}/billing/invoices/${log.obj.uuid}?entity=${log.company_entity.uuid}`
        : `/billing/invoices/${log.obj.uuid}?entity=${log.company_entity.uuid}`;
    }
    return isAdmin
      ? `/crm/${log.company_group?.uuid}/billing/invoices/`
      : `/billing/invoices/`;
  }

  if (log.obj?.content_type === AUDIT_LOG_CONTENT_TYPE.COMPANY_ENTITY_INVOICE) {
    const query = qs.stringify(
      { entity: log.company_entity?.uuid, company: log.company_group?.uuid },
      { skipNulls: true, addQueryPrefix: true }
    );
    return `/books/invoices-and-customers/${log.obj.uuid}${query}`;
  }

  if (
    log.obj?.content_type ===
    AUDIT_LOG_CONTENT_TYPE.COMPANY_GROUP_SUBSCRIPTION_DATA
  ) {
    if (log.company_entity) {
      return isAdmin
        ? `/crm/${log.company_group?.uuid}/billing?entity=${log.company_entity.uuid}`
        : `/billing?entity=${log.company_entity.uuid}`;
    }
    return isAdmin ? `/crm/${log.company_group?.uuid}/billing` : `/billing`;
  }

  if (log.obj?.content_type === AUDIT_LOG_CONTENT_TYPE.ENTITY_ITEM) {
    const query = qs.stringify(
      { entity: log.company_entity?.uuid, company: log.company_group?.uuid },
      { addQueryPrefix: true }
    );
    return `/books/data-sources/${log.obj.uuid}/${query}`;
  }

  if (log.obj?.content_type === AUDIT_LOG_CONTENT_TYPE.ENTITY_TRANSACTION) {
    const query = qs.stringify(
      {
        entity: log.company_entity?.uuid,
        company: log.company_group?.uuid,
        transaction_id: log.obj.uuid,
      },
      { addQueryPrefix: true }
    );
    return `/books/transactions/${query}`;
  }

  if (log.obj?.content_type === AUDIT_LOG_CONTENT_TYPE.TASK) {
    return `/tax/filings/${log.obj.uuid}`;
  }

  if (log.obj?.content_type === AUDIT_LOG_CONTENT_TYPE.CHARTS_OF_ACCOUNT) {
    const query = qs.stringify(
      {
        entity: log.company_entity?.uuid,
        company: log.company_group?.uuid,
        selected_category_id: log.obj.uuid,
      },
      { skipNulls: true, addQueryPrefix: true }
    );
    return `/books/chart-of-accounts${query}`;
  }

  if (log.obj?.content_type === AUDIT_LOG_CONTENT_TYPE.BOOKKEEPING_SETTINGS) {
    const query = qs.stringify(
      {
        tab: BOOKS_SETTINGS_TABS.ACCOUNTING,
        entity: log.company_entity?.uuid,
        company: log.company_group?.uuid,
      },
      { skipNulls: true, addQueryPrefix: true }
    );
    return `/books/books-settings${query}`;
  }

  if (log.obj?.content_type === AUDIT_LOG_CONTENT_TYPE.LEDGER_ENTRY) {
    const query = qs.stringify(
      {
        entity: log.company_entity?.uuid,
        company: log.company_group?.uuid,
        journal_entry_id: log.obj.uuid,
      },
      { skipNulls: true, addQueryPrefix: true }
    );
    return `/books/journal-entries${query}`;
  }

  return null;
};

export const AuditLogSlider = ({
  log,
  onClose,
}: {
  log: AuditLog | null;
  onClose: () => void;
}) => {
  const { isAdmin } = useRoleBasedView();

  const redirectLink = log ? getRedirectLink({ log, isAdmin }) : null;

  const title = log?.obj?.description || log?.event.name;

  return (
    <Slider.Root open={Boolean(log)} onOpenChange={onClose}>
      <Slider.Content>
        <Slider.Header>
          <Slider.Title className="t-pr-6">{title}</Slider.Title>
          <div className="t-flex t-items-center t-gap-1">
            {redirectLink && (
              <Link to={redirectLink} target="_blank">
                <Button size="xsmall" customType="ghost">
                  <Redirect size={15} strokeWidth="1" />
                </Button>
              </Link>
            )}
            <Slider.Close />
          </div>
        </Slider.Header>
        <Slider.Body className="!t-p-0 t-h-full">
          {log && (
            <div className="t-pt-5">
              <AuditlogList
                entityId={log.company_entity?.uuid || ""}
                objectId={log.obj?.uuid || ""}
              />
            </div>
          )}
        </Slider.Body>
      </Slider.Content>
    </Slider.Root>
  );
};
